import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';;
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useAuth } from './AuthContext';
import type { InboxItem } from '../hooks/useInboxList';

type InboxListResponse = {
  data: InboxItem[];
  meta?: { unseen?: number; total?: number };
};

type InboxCountContextValue = {
  count: number;
  unseenCount: number;
  refresh: () => void;
  syncCounts: (payload: { total: number; unseen: number }) => void;
};

const Ctx = createContext<InboxCountContextValue | null>(null);

export function InboxCountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [unseenCount, setUnseenCount] = useState(0);

  const syncCounts = useCallback(({ total, unseen }: { total: number; unseen: number }) => {
    setCount(total);
    setUnseenCount(unseen);
  }, []);

  const refresh = useCallback(() => {
    const token = getToken();
    if (!token || !user) return;
    void apiGet<InboxListResponse>(`/inbox`, token)
      .then((b) => {
        const list = unwrapData<InboxItem[]>(b);
        const meta = (b as InboxListResponse).meta;
        const unseen =
          typeof meta?.unseen === 'number'
            ? meta.unseen
            : list.filter((i) => !i.seen).length;
        setCount(list.length);
        setUnseenCount(unseen);
      })
      .catch((e) => {
        if (import.meta.env.DEV) console.error('[InboxCount] poll failed:', getDisplayError(e), e);
      });
  }, [user]);

  const value = useMemo(
    () => ({ count, unseenCount, refresh, syncCounts }),
    [count, unseenCount, refresh, syncCounts],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useInboxCount(): InboxCountContextValue {
  const v = useContext(Ctx);
  if (!v) {
    return { count: 0, unseenCount: 0, refresh: () => {}, syncCounts: () => {} };
  }
  return v;
}
